'use client'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    useSyncExternalStore,
    type ReactNode,
} from 'react'

import { useClientEnv } from '@/app/hooks/use-client-env'
import { type SessionContext } from '@/services/auth'
import { performLogout } from '@/services/auth/client/logout'
import {
    clearMirror,
    getServerSessionSnapshot,
    getSessionSnapshot,
    readMirrorToken,
    subscribeToMirror,
} from '@/services/auth/client/session-mirror'
import { setSnowplowUserId } from '@/services/snowplow/set-user-id'

interface SessionContextValue {
    session: SessionContext
    /** Log out in place (client-direct): WAPI logout + clear mirror + clear analytics identity. */
    logout: () => Promise<void>
}

const SessionStateContext = createContext<SessionContextValue | null>(null)

/**
 * Provides client session state to the React tree. The session is read from the
 * localStorage mirror via `useSyncExternalStore`: the **server snapshot is always
 * logged-out** (so SSR/SSG HTML stays session-blind and cached pages remain
 * byte-identical), and the client snapshot resolves the real state after
 * hydration. Cross-tab mirror changes update it live. Sits beside `ClientEnvProvider`.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
    // `useClientEnv()` is null until ClientEnvProvider's init effect runs (which fires
    // *after* this child's effects). Reading it via context — and keeping it in the
    // greeting effect's deps — means the fetch fires once the env resolves, with no
    // dependency on a pre-hydration inline config script (the prior `getClientEnv()` +
    // `ClientConfigScript` race that left the greeting stuck on the generic label).
    const env = useClientEnv()

    const mirrorSession = useSyncExternalStore(
        subscribeToMirror,
        getSessionSnapshot,
        getServerSessionSnapshot,
    )

    // The name is not in the mirror — fetch it from userinfo.json post-hydration so the
    // navbar can greet by name (FR-15). Best-effort: a failed fetch leaves no name, and the
    // navbar falls back to the generic logged-in label (never logged-out).
    const [profile, setProfile] = useState<{ firstName?: string; lastName?: string }>({})

    useEffect(() => {
        if (!mirrorSession.isAuthenticated || !env) {
            return
        }
        const token = readMirrorToken()
        if (!token) {
            return
        }
        let cancelled = false
        env.services.wapi
            .getUserInfo(token)
            .then((info) => {
                if (!cancelled) {
                    setProfile({ firstName: info.firstName, lastName: info.lastName })
                }
            })
            .catch((err: unknown) => {
                // best-effort: greeting falls back to the generic label, but log so a
                // systematic userinfo failure is visible (low-cardinality metadata only).
                env.logger
                    .withMetadata({ error: err instanceof Error ? err.name : 'unknown' })
                    .debug('auth: userinfo greeting fetch failed')
            })
        return () => {
            cancelled = true
        }
    }, [mirrorSession.isAuthenticated, env])

    // Surface the fetched name only while authenticated — a stale profile from a previous
    // session is ignored when logged out (and overwritten on the next login's fetch).
    const session: SessionContext = useMemo(
        () =>
            mirrorSession.isAuthenticated
                ? {
                      ...mirrorSession,
                      ...(profile.firstName ? { firstName: profile.firstName } : {}),
                      ...(profile.lastName ? { lastName: profile.lastName } : {}),
                  }
                : mirrorSession,
        [mirrorSession, profile.firstName, profile.lastName],
    )

    // Logout needs the WAPI service + Datadog from the client env. By the time the user can
    // trigger it the env has resolved; guard the (transient) null so it never throws.
    const logout = useCallback(async () => {
        if (!env) {
            return
        }
        await performLogout({
            token: readMirrorToken(),
            logoutRequest: (token) => env.services.wapi.logout(token),
            clearMirror,
            setSnowplowUserId,
            clearRumUser: () => {
                env.services.datadog.clearUser()
            },
        })
    }, [env])

    return (
        <SessionStateContext.Provider value={{ session, logout }}>
            {children}
        </SessionStateContext.Provider>
    )
}

export function useSession(): SessionContextValue {
    const context = useContext(SessionStateContext)
    if (!context) {
        throw new Error('useSession must be used within a SessionProvider')
    }
    return context
}
